import React, { useEffect, useState, useCallback } from "react";
import { API_URL } from "../../config";
import Notification from "../../components/Notification";
import ConfirmDialog from "../../components/ConfirmDialog";
import { allowedToView } from "../../utils/PermissionUtils";
import { useLocation, useNavigate } from "react-router-dom";

function Terms() {
    const [terms, setTerms] = useState({
        data: [],
        current_page: 1,
        per_page: 10,
        total: 0,
        last_page: 1
    });
    const [newTerm, setNewTerm] = useState({
        term_number: "",
        year: new Date().getFullYear(),
        is_current: false
    });
    const [editTerm, setEditTerm] = useState(null);
    const [notification, setNotification] = useState({ type: "", message: "" });
    const [errors, setErrors] = useState({});
    // const [currentTermId, setCurrentTermId] = useState(null);
    const [isConfirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [selectedTerm, setSelectedTerm] = useState(null);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10
    });
    const navigate = useNavigate();
    const location = useLocation();
    const token = localStorage.getItem("access_token");
    const userRole = localStorage.getItem("userRole");

    const canEdit = (userRole) => {
        return allowedToView(userRole, ['admin', 'superadmin', 'missions-coordinator']);
    };

    const fetchTerms = useCallback(async () => {
        try {
            const { page, limit } = pagination;
            const url = new URL(`${API_URL}/terms`);
            url.searchParams.append('page', page);
            url.searchParams.append('limit', limit);

            const response = await fetch(url, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
            });

            if (response.ok) {
                const data = await response.json();
                setTerms(data.data || {
                    data: [],
                    current_page: 1,
                    per_page: 10,
                    total: 0,
                    last_page: 1
                });

                // Find and set the current term
                // const current = (data.data?.data || []).find(term => term.is_current);
                // if (current) setCurrentTermId(current.id);
            } else if (response.status === 401) {
                navigate("/logout", { state: { from: location } });
            } else {
                const errorData = await response.json();
                setErrors(errorData || {});
                setNotification({
                    message: errorData.message || "An error occurred.",
                    type: "danger"
                });
            }
        } catch (error) {
            console.error("Error fetching terms:", error);
            setNotification({
                type: "danger",
                message: "Failed to fetch terms."
            });
        }
    }, [token, pagination, navigate, location]);

    useEffect(() => {
        fetchTerms();
    }, [fetchTerms]);

    const handlePaginationChange = (newPage, newLimit) => {
        setPagination(prev => ({
            page: newPage !== undefined ? newPage : prev.page,
            limit: newLimit !== undefined ? newLimit : prev.limit
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrors({});

        try {
            const response = editTerm
                ? await updateTerm(editTerm.id, newTerm)
                : await createTerm(newTerm);

            const responseData = await response.json();
            /*
                        if (!response.ok) {
                            if (response.status === 422 && responseData.errors) {
                                setErrors(responseData.errors);
                            } else {
                                setNotification({
                                    type: "danger",
                                    message: responseData.message || "An error occurred"
                                });
                            }
                            return;
                        } 
                        */
            if (response.ok) {
                const message = editTerm
                    ? "Term updated successfully."
                    : "Term added successfully.";

                setNotification({ type: "success", message });
                setNewTerm({
                    term_number: "",
                    year: new Date().getFullYear(),
                    is_current: false
                });
                setEditTerm(null);
                fetchTerms();
            } else if (response.status === 404) {
                setNotification({ type: "danger", message: "Error updating term" });
            } else if (response.status === 401) {
                navigate("/logout", { state: { from: location } });
            } else if (response.status === 422 && responseData.errors) {
                setErrors(responseData.errors);
            } else {
                setNotification({
                    type: "danger",
                    message: responseData.message || "An error occurred"
                });
            }

        } catch (error) {
            console.error("Error submitting term:", error);
            setNotification({
                type: "danger",
                message: "Failed to submit the term."
            });
        }
    };

    const createTerm = async (termData) => {
        const response = await fetch(`${API_URL}/terms`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
            },
            body: JSON.stringify(termData),
        });
        return response;
    };

    const updateTerm = async (id, termData) => {
        const response = await fetch(`${API_URL}/terms/${id}`, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
            },
            body: JSON.stringify(termData),
        });
        return response;
    };

    const handleSetCurrent = async (termId) => {
        try {
            const response = await fetch(`${API_URL}/terms/${termId}/set-current`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
                body: JSON.stringify({ is_current: true }),
            });

            if (!response.ok) {
                throw new Error("Failed to set current term");
            }

            // setCurrentTermId(termId);
            fetchTerms();
            setNotification({
                type: "success",
                message: "Current term updated successfully."
            });
        } catch (error) {
            console.error("Error setting current term:", error);
            setNotification({
                type: "danger",
                message: "Failed to set current term."
            });
        }
    };

    const handleEdit = (term) => {
        setNewTerm({
            term_number: term.term_number,
            year: term.year,
            is_current: term.is_current
        });
        setEditTerm(term);
    };

    const handleDeleteClick = (term) => {
        setSelectedTerm(term);
        setConfirmDialogOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!selectedTerm) return;

        try {
            const response = await fetch(`${API_URL}/terms/${selectedTerm.id}`, {
                method: 'DELETE',
                headers: {
                    "Authorization": `Bearer ${token}`,
                },
            });

            const responseData = await response.json();

            if (response.ok) {
                setNotification({
                    type: "success",
                    message: 'Term deleted successfully'
                });
                fetchTerms();
            } else if (response.status === 422) {
                setNotification({
                    type: "danger",
                    message: responseData.message
                });
            } else if (response.status === 401) {
                navigate("/logout", { state: { from: location } });
            } else {
                setNotification({
                    type: "danger",
                    message: responseData.message || 'Failed to delete term'
                });
            }
        } catch (error) {
            setNotification({
                type: "danger",
                message: 'An error occurred while deleting the term'
            });
            console.error("Error:", error);
        } finally {
            setConfirmDialogOpen(false);
            setSelectedTerm(null);
        }
    };

    const handleCancelDelete = () => {
        setConfirmDialogOpen(false);
        setSelectedTerm(null);
    };

    const closeNotification = () => {
        setNotification({ type: "", message: "" });
    };

    return (
        <main>
            <nav className="level">
                <div className="level-left">
                    <div className="level-item">
                        <p className="title">Terms</p>
                    </div>
                </div>
            </nav>

            <div className="columns">
                <div className={`column ${canEdit(userRole) ? 'is-half' : 'is-fullwidth'} dash-section vertical-separator`}>
                    <h1 className="section-title">Terms</h1>

                    {notification.message && (
                        <Notification
                            type={notification.type}
                            message={notification.message}
                            onClose={closeNotification}
                        />
                    )}

                    <table className="table is-striped is-fullwidth">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Name</th>
                                <th>Year</th>
                                <th>Status</th>
                                {canEdit(userRole) && <th>Options</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {terms.data?.map((term, index) => (
                                <tr key={term.id}>
                                    <td>{(terms.current_page - 1) * terms.per_page + index + 1}</td>
                                    <td>{term.name}</td>
                                    <td>{term.year}</td>
                                    <td>
                                        {term.is_current ? (
                                            <span className="tag is-success">Current</span>
                                        ) : (
                                            <span className="tag is-light">Inactive</span>
                                        )}
                                    </td>
                                    {canEdit(userRole) && (
                                        <td>
                                            <div className="buttons">
                                                <button
                                                    className="button is-small is-white"
                                                    onClick={() => handleEdit(term)}
                                                    title="Edit"
                                                >
                                                    <span className="icon">
                                                        <i className="fas fa-edit"></i>
                                                    </span>
                                                </button>
                                                <button
                                                    className="button is-small is-white"
                                                    onClick={() => handleDeleteClick(term)}
                                                    title="Delete"
                                                >
                                                    <span className="icon">
                                                        <i className="fas fa-trash-alt"></i>
                                                    </span>
                                                </button>
                                                {!term.is_current && (
                                                    <button
                                                        className="button is-small is-white"
                                                        onClick={() => handleSetCurrent(term.id)}
                                                        title="Set as Current"
                                                    >
                                                        <span className="icon">
                                                            <i className="fas fa-star"></i>
                                                        </span>
                                                    </button>
                                                )}
                                                {term.is_current && (
                                                    <span className="icon has-text-success" title="Current Term">
                                                        <i className="fas fa-star"></i>
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    {terms.last_page > 1 && (
                        <nav className="pagination is-centered" role="navigation" aria-label="pagination">
                            <button
                                className="pagination-previous"
                                disabled={terms.current_page === 1}
                                onClick={() => handlePaginationChange(terms.current_page - 1)}
                            >
                                Previous
                            </button>
                            <button
                                className="pagination-next"
                                disabled={terms.current_page === terms.last_page}
                                onClick={() => handlePaginationChange(terms.current_page + 1)}
                            >
                                Next
                            </button>
                            <ul className="pagination-list">
                                {Array.from({ length: terms.last_page }, (_, i) => i + 1).map(page => (
                                    <li key={page}>
                                        <button
                                            className={`pagination-link ${terms.current_page === page ? 'is-current' : ''}`}
                                            onClick={() => handlePaginationChange(page)}
                                        >
                                            {page}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </nav>
                    )}
                </div>

                {canEdit(userRole) && (
                    <div className="column is-half dash-section">
                        <h1 className="section-title">{editTerm ? "Edit Term" : "Add New Term"}</h1>

                        <form onSubmit={handleSubmit}>
                            <div className="field">
                                <label className="label">Term Number</label>
                                <div className="control has-icons-left">
                                    <div className="select is-fullwidth">
                                        <select
                                            className={errors.term_number ? "is-danger" : ""}
                                            value={newTerm.term_number}
                                            onChange={(e) => setNewTerm({ ...newTerm, term_number: e.target.value })}
                                            required
                                        >
                                            <option value="">Select Term</option>
                                            <option value="1">Term 1</option>
                                            <option value="2">Term 2</option>
                                            <option value="3">Term 3</option>
                                        </select>
                                    </div>
                                    <span className="icon is-left">
                                        <i className="fas fa-list-ol"></i>
                                    </span>
                                </div>
                                {errors.term_number && (
                                    <p className="help is-danger">{errors.term_number[0]}</p>
                                )}
                            </div>

                            <div className="field">
                                <label className="label">Year</label>
                                <div className="control has-icons-left">
                                    <input
                                        className={`input ${errors.year ? "is-danger" : ""}`}
                                        type="number"
                                        min="2020"
                                        max={new Date().getFullYear() + 1}
                                        value={newTerm.year}
                                        onChange={(e) => setNewTerm({ ...newTerm, year: e.target.value })}
                                        required
                                    />
                                    <span className="icon is-small is-left">
                                        <i className="fas fa-calendar-alt"></i>
                                    </span>
                                </div>
                                {errors.year && (
                                    <p className="help is-danger">{errors.year[0]}</p>
                                )}
                            </div>

                            <div className="field">
                                <label className="checkbox">
                                    <input
                                        type="checkbox"
                                        checked={newTerm.is_current}
                                        onChange={(e) => setNewTerm({ ...newTerm, is_current: e.target.checked })}
                                    />
                                    <span className="ml-2">Set as current term</span>
                                </label>
                                {errors.is_current && (
                                    <p className="help is-danger">{errors.is_current[0]}</p>
                                )}
                            </div>

                            <div className="field">
                                <div className="control">
                                    <button type="submit" className="button primary-bg">
                                        {editTerm ? "Update Term" : "Add Term"}
                                    </button>
                                    {editTerm && (
                                        <button
                                            type="button"
                                            className="button is-light ml-2"
                                            onClick={() => {
                                                setEditTerm(null);
                                                setNewTerm({
                                                    term_number: "",
                                                    year: new Date().getFullYear(),
                                                    is_current: false
                                                });
                                            }}
                                        >
                                            Cancel
                                        </button>
                                    )}
                                </div>
                            </div>
                        </form>
                    </div>
                )}
            </div>

            <ConfirmDialog
                isOpen={isConfirmDialogOpen}
                theme="is-danger"
                title="Delete Confirmation"
                message={`Are you sure you want to delete ${selectedTerm?.name} (Term ${selectedTerm?.term_number}, ${selectedTerm?.year})?`}
                onConfirm={handleConfirmDelete}
                onCancel={handleCancelDelete}
                isLoading={false}
            />
        </main>
    );
}

export default Terms;